/*
 * The maximized Casey sidebar's slices of the thread list: newest first in
 * Today, Yesterday, Previous 7 Days and Older, the way chat apps list
 * history. Pure so the rules are testable.
 */
import type { AgentThread } from "@/features/agents/api/agentApi";
import { threadMatchesQuery } from "@/features/agents/lib/threadGroups";
import { recencyBucket, type RecencyBucket } from "@/features/agents/lib/time";

import { readWorkflowPrompt } from "../workflows/catalog";

export const SIDEBAR_GROUP_LABEL: Record<RecencyBucket, string> = {
  today: "Today",
  yesterday: "Yesterday",
  week: "Previous 7 Days",
  older: "Older"
};

const ORDER: RecencyBucket[] = ["today", "yesterday", "week", "older"];

export type ConversationGroup = {
  id: RecencyBucket;
  label: string;
  threads: AgentThread[];
};

const stamp = (t: AgentThread) => t.updated_at ?? t.created_at ?? "";

/** Unarchived threads matching `query`, newest first, in recency groups; empty groups omitted. */
export const groupConversations = (
  threads: AgentThread[],
  query = "",
  now: number = Date.now()
): ConversationGroup[] => {
  const buckets = new Map<RecencyBucket, AgentThread[]>();
  const sorted = threads
    .filter((t) => !t.archived && threadMatchesQuery(t, query))
    .sort((a, b) => stamp(b).localeCompare(stamp(a)));
  for (const t of sorted) {
    const id = recencyBucket(stamp(t), now);
    buckets.set(id, [...(buckets.get(id) ?? []), t]);
  }
  return ORDER.filter((id) => buckets.has(id)).map((id) => ({
    id,
    label: SIDEBAR_GROUP_LABEL[id],
    threads: buckets.get(id) ?? []
  }));
};

/** The case's workflow thread, or one a workflow prompt started. */
export const isWorkflowThread = (
  thread: AgentThread,
  workflowThreadId: string | null
): boolean =>
  (workflowThreadId !== null && thread.id === workflowThreadId) ||
  readWorkflowPrompt(thread.title) !== null;

/** Waiting on an approval or an answer from the user. */
export const needsYou = (thread: AgentThread): boolean =>
  Boolean(thread.pending_approval || thread.pending_question);

/** What a sidebar row stands for, which picks its icon. */
export type ConversationKind = "workflow" | "automation" | "chat";

/** A workflow thread, an automation run's transcript, or a plain chat. */
export const conversationKind = (
  thread: AgentThread,
  workflowThreadId: string | null
): ConversationKind => {
  if (isWorkflowThread(thread, workflowThreadId)) return "workflow";
  if (thread.skill_run_id) return "automation";
  return "chat";
};

const WORKFLOW_PREFIX = "CaseBlink Workflow";

/**
 * A row's display title and quiet subtitle. "CaseBlink Workflow · Research"
 * reads as the title "CaseBlink Workflow" with the subtitle "Research", so a
 * column of workflow threads is told apart at a glance. Display only: the
 * stored title is never changed.
 */
export const splitWorkflowTitle = (
  title: string
): { title: string; subtitle: string | null } => {
  const text = title.trim();
  if (!text.startsWith(WORKFLOW_PREFIX)) return { title, subtitle: null };
  const rest = text
    .slice(WORKFLOW_PREFIX.length)
    .replace(/^\s*[·•:|-]\s*/, "")
    .trim();
  return { title: WORKFLOW_PREFIX, subtitle: rest || null };
};

const clock = (ms: number) =>
  new Date(ms).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit"
  });

/**
 * The quiet time on a row: the clock time today ("9:53 AM"), "Yesterday",
 * the weekday within the week, else a short date. Empty without a time.
 */
export const rowTimeLabel = (
  iso: string | null | undefined,
  now: number = Date.now()
): string => {
  if (!iso) return "";
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return "";
  const bucket = recencyBucket(iso, now);
  if (bucket === "today") return clock(at);
  if (bucket === "yesterday") return "Yesterday";
  const date = new Date(at);
  if (bucket === "week")
    return date.toLocaleDateString("en-US", { weekday: "short" });
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === new Date(now).getFullYear()
      ? {}
      : { year: "numeric" })
  });
};
